import { Menu, Transition } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { classNames } from "@/src/utils/cn";
import { PermissionToggleOption } from "./dealPermissionConfig";

export function PermissionLevelDropdown(props: {
  options: PermissionToggleOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  const selected = props.options.find((o) => o.value === props.value);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isOpen]);

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => {
        if (open !== isOpen) {
          setIsOpen(open);
        }

        return (
          <>
            <Menu.Button
              ref={buttonRef}
              disabled={props.disabled}
              className={classNames(
                "inline-flex items-center gap-x-1 text-sm font-semibold outline-none",
                props.disabled
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-600 hover:text-gray-800",
              )}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {selected?.label ?? "Select"}
              <ChevronDown
                className="w-4 h-4 text-gray-500"
                aria-hidden="true"
              />
            </Menu.Button>

            {isOpen &&
              createPortal(
                <div
                  style={{
                    position: "absolute",
                    top: position.top,
                    right: position.right,
                    zIndex: 9999,
                  }}
                >
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none min-w-max py-1">
                      {props.options.map((o) => (
                        <Menu.Item key={o.value}>
                          {({ active }) => (
                            <button
                              type="button"
                              className={classNames(
                                "flex w-full items-center justify-between gap-x-6 px-3 py-1.5 text-sm text-left",
                                active
                                  ? "bg-gray-50 text-gray-900"
                                  : "text-gray-700",
                                o.value === props.value
                                  ? "font-semibold"
                                  : "font-normal",
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                props.onChange(o.value);
                              }}
                            >
                              <span>{o.label}</span>
                              {o.value === props.value ? (
                                <Check className="w-4 h-4 text-persian-600" />
                              ) : (
                                <span className="w-4" />
                              )}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </div>,
                document.body,
              )}
          </>
        );
      }}
    </Menu>
  );
}
